import type { CapitalPlanService, PortfolioPlan } from "./capitalPlanService";
import type { DbExecutor } from "./executor";
import type { ProjectPlan } from "../engine/capitalPlan";
import type { FinancialCalendarModel } from "../engine/financialCalendar";
import type { LoanLedger } from "../engine/financing";

export type PortfolioSnapshot = Omit<PortfolioPlan,"plans"> & {plans:Array<Omit<ProjectPlan,"ledgers"> & {ledgers:Array<[string,LoanLedger]>}>};
export function encodePortfolioSnapshot(plan:PortfolioPlan):PortfolioSnapshot {
  return {...plan,plans:plan.plans.map(project=>({...project,ledgers:[...project.ledgers.entries()]}))};
}
export function decodePortfolioSnapshot(plan:PortfolioSnapshot):PortfolioPlan {
  if(!plan || !Array.isArray(plan.plans) || plan.plans.some(project=>!Array.isArray(project.ledgers)||project.ledgers.some(entry=>!Array.isArray(entry)||entry.length!==2||typeof entry[0]!=="string")))throw Error("The financial snapshot could not be read.");
  return {...plan,plans:plan.plans.map(project=>({...project,ledgers:new Map(project.ledgers)}))};
}
export const validSnapshotDate=(value:unknown):value is string=>typeof value==="string"&&/^\d{4}-\d{2}-\d{2}$/.test(value)&&Number.isFinite(Date.parse(`${value}T00:00:00Z`))&&new Date(`${value}T00:00:00Z`).toISOString().slice(0,10)===value;
export const supportedSnapshotDate=(date:string,today:string)=>Math.abs(Date.parse(`${date}T00:00:00Z`)-Date.parse(`${today}T00:00:00Z`))<=3660*86400000;

/** Hosted transport only. Unsupported optimization bounds preserve the original
 * service path; authorization, validation and server failures never fall back. */
export function useHostedFinancialSnapshots(service:CapitalPlanService,db:Pick<DbExecutor,"whenIdle">,options:{request?:typeof fetch;onUnauthorized?:()=>void}={}):void {
  const request=options.request??fetch;
  const originalPortfolio=service.portfolio.bind(service),originalCalendar=service.calendar.bind(service);
  async function read<T>(path:string,body:unknown):Promise<{supported:true;snapshot:T}|{supported:false}> {
    await db.whenIdle?.();
    let response:Response;
    try{response=await request(path,{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});}
    catch{throw Error("The financial snapshot could not be loaded. Check your connection and try again.");}
    if(response.status===401)options.onUnauthorized?.();
    const payload=await response.json().catch(()=>null) as {code?:string;error?:string;snapshot?:T}|null;
    if(response.status===422&&payload?.code==="snapshot-unavailable")return {supported:false};
    if(!response.ok)throw Error(payload?.error??"The financial snapshot could not be loaded.");
    if(!payload||!("snapshot" in payload))throw Error("The financial snapshot could not be read.");
    return {supported:true,snapshot:payload.snapshot as T};
  }
  service.portfolio=async(variant="expected",horizonEnd)=>{
    const result=await read<PortfolioSnapshot>("/api/financial/portfolio",{variant,...(horizonEnd===undefined?{}:{horizonEnd})});
    return result.supported?decodePortfolioSnapshot(result.snapshot):originalPortfolio(variant,horizonEnd);
  };
  service.calendar=async(projectId,from,to,asOf=new Date().toISOString().slice(0,10))=>{
    const result=await read<FinancialCalendarModel>("/api/financial/calendar",{projectId,from,to,asOf});
    return result.supported?result.snapshot:originalCalendar(projectId,from,to,asOf);
  };
}
